import { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';
import { Input } from '@/components/ui/input';
import Logo from '@/components/Logo';
import SEOHead from '@/components/SEOHead';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const FAQ = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState<number[]>([]);

  const faqs: FAQItem[] = [
    // Pricing & Plans
    {
      category: 'Pricing & Plans',
      question: 'How much does Dine AI cost?',
      answer: 'We offer three plans: Yummy (starting at $39.99/month for 1 location), Delicious (starting at $59.99/month for 2 locations with 30-day free trial), and Gourmet (custom pricing for 5+ locations). Annual billing saves you 20% - that\'s $31.99/month for Yummy and $47.99/month for Delicious when billed annually.'
    },
    {
      category: 'Pricing & Plans',
      question: 'Is there a free trial?',
      answer: 'Yes! Our Delicious plan comes with a 30-day free trial. You can cancel anytime during the trial with no charges.'
    },
    {
      category: 'Pricing & Plans',
      question: 'Can I change plans later?',
      answer: 'Absolutely! You can upgrade to a higher plan anytime directly from your dashboard. To downgrade to a lower plan, click "Manage Subscription" in your settings, which will take you to our secure Stripe billing portal where you can select a different plan. All prorated charges/credits are calculated automatically.'
    },
    {
      category: 'Pricing & Plans',
      question: 'What payment methods do you accept?',
      answer: 'We accept all major credit cards (Visa, Mastercard, American Express, Discover) and Bank ACH transfers through our secure Stripe payment processing.'
    },
    {
      category: 'Pricing & Plans',
      question: 'Do you offer discounts for annual billing?',
      answer: 'Yes! Annual billing saves you 20% compared to monthly billing. That\'s like getting 2+ months free!'
    },

    // Getting Started
    {
      category: 'Getting Started',
      question: 'How do I get started with Dine AI?',
      answer: 'Simply sign up with your Google, Apple, or email account, upload your menu PDF, and our AI will extract and digitize your menu in minutes. Then have your customers download Dine AI app from Apple or Play Store and see Dine AI in action right away with AI recommendations.'
    },
    {
      category: 'Getting Started',
      question: 'How long does it take to set up?',
      answer: 'Most restaurants are up and running in under 3-5 minutes! Our AI handles the heavy lifting of menu extraction, so you just need to review and publish.'
    },
    {
      category: 'Getting Started',
      question: 'What format should my menu be in?',
      answer: 'We accept PDF files, images (JPG, PNG), and can even search for your menu online using our Google Places integration.'
    },
    {
      category: 'Getting Started',
      question: 'Do I need technical skills to use Dine AI?',
      answer: 'Not at all! Unlike complex POS systems or restaurant management software, Dine AI is designed for simplicity. A few clicks to set up, then you\'re in cruise mode - our AI handles menu optimization, customer recommendations, and provides actionable insights automatically. Your focus stays on what matters: great food and happy customers, while we help maximize your revenue.'
    },

    // Features & Capabilities
    {
      category: 'Features & Capabilities',
      question: 'What is AI image generation?',
      answer: 'Our AI creates professional food photos for your menu items. Each plan includes a yearly quota of AI-generated images (50-500 depending on your plan). You can also upload your own photos.'
    },
    {
      category: 'Features & Capabilities',
      question: 'Can I customize my menu display?',
      answer: 'Yes! You can organize sections, add descriptions, set prices, mark items as featured, and customize dietary/allergen filters to match your restaurant\'s needs.'
    },
    {
      category: 'Features & Capabilities',
      question: 'How does the analytics dashboard work?',
      answer: 'Our analytics show you popular items, customer preferences, search patterns, and revenue insights. Available on Yummy, Delicious and Gourmet plans.'
    },
    {
      category: 'Features & Capabilities',
      question: 'Can customers order through Dine AI?',
      answer: 'Yes! Customers can place dine-in orders through the Dine AI mobile app from anywhere - even on their way to your restaurant. When they arrive, they simply show a QR code to the waiter, who scans it to confirm the order. It\'s a seamless experience that reduces wait times and improves table turnover.'
    },
    {
      category: 'Features & Capabilities',
      question: 'Do you support multiple locations?',
      answer: 'Yes! Delicious plan supports up to 2 locations, and Gourmet plan supports 5+ locations. Each location has its own menu and analytics.'
    },
    {
      category: 'Features & Capabilities',
      question: 'Can I filter menu items by dietary restrictions?',
      answer: 'Yes! You can tag items as vegetarian, vegan, gluten-free, etc. Customers can then filter the menu based on their dietary needs.'
    },

    // Technical & Integrations
    {
      category: 'Technical & Integrations',
      question: 'Does Dine AI integrate with POS systems?',
      answer: 'POS integration is available on request for Gourmet plan customers. Contact our team at support@dineai.ai to discuss your specific needs.'
    },
    {
      category: 'Technical & Integrations',
      question: 'Is my data secure?',
      answer: 'Absolutely! We use bank-level encryption (SSL/TLS), secure cloud storage, and comply with industry-standard security practices. Your data is never shared with third parties.'
    },
    {
      category: 'Technical & Integrations',
      question: 'Can I export my menu data?',
      answer: 'Yes, you can export your menu data at any time from your dashboard. We believe your data belongs to you!'
    },
    {
      category: 'Technical & Integrations',
      question: 'Do you have an API?',
      answer: 'API access is available for Gourmet plan customers. Contact support@dineai.ai to discuss your integration needs.'
    },

    // Support & Account
    {
      category: 'Support & Account',
      question: 'What kind of support do you offer?',
      answer: 'All plans include email support. Delicious and Gourmet plans get priority support with faster response times. We typically respond within 24 hours.'
    },
    {
      category: 'Support & Account',
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel anytime from your dashboard. No contracts, no cancellation fees. If you cancel during a free trial, you won\'t be charged.'
    },
    {
      category: 'Support & Account',
      question: 'What happens if I cancel?',
      answer: 'Your menu will remain active until the end of your billing period. After that, your menu will be unpublished, but your data is retained for 90 days in case you want to reactivate.'
    },
    {
      category: 'Support & Account',
      question: 'Do you offer training or onboarding help?',
      answer: 'Yes! We provide setup assistance and video tutorials. Gourmet plan customers receive personalized onboarding support.'
    },
    {
      category: 'Support & Account',
      question: 'How do I update my menu?',
      answer: 'Simply upload a new menu PDF, or edit items directly in your dashboard. Changes are reflected on your customer-facing menu instantly.'
    },

    // Customer Experience
    {
      category: 'Customer Experience',
      question: 'How do customers access my menu?',
      answer: 'Customers can access your menu through the Dine AI mobile app (iOS/Android) on their own devices, or you can install the app on tablets and use them as digital menus at your restaurant for customers to order directly. Our AI assistant learns their preferences - dietary needs, favorite cuisines, past orders - and provides personalized recommendations for a better dining experience. They can search for your restaurant by name or location, and get tailored suggestions the moment they open your menu.'
    },
    {
      category: 'Customer Experience',
      question: 'Can customers save favorite items?',
      answer: 'Yes! Customers can create accounts, save favorite dishes, and get personalized recommendations based on their preferences.'
    },
    {
      category: 'Customer Experience',
      question: 'Do you support multiple languages?',
      answer: 'Currently we support English. Multi-language support is on our roadmap for future releases.'
    },
    {
      category: 'Customer Experience',
      question: 'Can customers see real-time menu availability?',
      answer: 'Menu availability updates are available for all plans! The moment you update your menu from your Dashboard, all items are instantly available in the Dine AI Menu Assistant. You can also manually delete items that are unavailable from the edit menu on your dashboard.'
    },

    // Business & Growth
    {
      category: 'Business & Growth',
      question: 'How does Dine AI help increase revenue?',
      answer: 'Our AI-powered recommendations, menu optimization insights, and beautiful presentation help customers discover more items, leading to higher order values and repeat visits.'
    },
    {
      category: 'Business & Growth',
      question: 'Can I see which items are most popular?',
      answer: 'Yes! All plans include our Dine AI Impact Dashboard showing popular items, search trends, and customer preferences, plus AI Performance Analysis that automatically analyzes your data weekly and generates 3 actionable insights to optimize your operations, like pricing recommendations, menu mix optimization, and demand patterns. Customer order intelligence is available to Delicious and Gourmet customers. It\'s like having a data analyst on your team!'
    },
    {
      category: 'Business & Growth',
      question: 'Do you offer marketing features?',
      answer: 'Not yet, but we\'re working on email campaigns, promotional features, and customer engagement tools for future releases.'
    }
  ];

  const toggleItem = (index: number) => {
    setOpenItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const categories = Array.from(new Set(faqs.map(faq => faq.category)));

  // Generate FAQPage structured data for SEO
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <SEOHead
        title="FAQ - Frequently Asked Questions | Dine AI"
        description="Find answers to common questions about Dine AI's menu management platform, pricing, features, and support."
        structuredData={faqStructuredData}
      />

      <div className="min-h-screen bg-gradient-to-b from-dineai-blue to-[#1a3ba8]">
        {/* Header */}
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center mb-12">
            <Logo className="text-white h-10" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-white/80 max-w-2xl mx-auto">
              Everything you need to know about Dine AI
            </p>
          </motion.div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="max-w-2xl mx-auto mb-12"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white/60 w-5 h-5 z-10 pointer-events-none" />
              <Input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-6 bg-white/10 border-white/20 text-white placeholder:text-white/50 rounded-xl text-lg backdrop-blur-sm focus:bg-white/20 relative"
              />
            </div>
          </motion.div>

          {/* FAQ Categories */}
          <div className="max-w-4xl mx-auto">
            {categories.map((category, categoryIndex) => {
              const categoryFaqs = filteredFaqs.filter(faq => faq.category === category);

              if (categoryFaqs.length === 0) return null;

              return (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + categoryIndex * 0.1 }}
                  className="mb-8"
                >
                  <h2 className="text-2xl font-bold text-dineai-yellow mb-4">
                    {category}
                  </h2>
                  <div className="space-y-3">
                    {categoryFaqs.map((faq, index) => {
                      const globalIndex = faqs.indexOf(faq);
                      const isOpen = openItems.includes(globalIndex);

                      return (
                        <motion.div
                          key={globalIndex}
                          className="bg-white/10 backdrop-blur-sm rounded-xl border border-white/20 overflow-hidden hover:bg-white/15 transition-colors"
                        >
                          <button
                            onClick={() => toggleItem(globalIndex)}
                            className="w-full px-6 py-4 flex items-center justify-between text-left"
                          >
                            <span className="text-lg font-semibold text-white pr-4">
                              {faq.question}
                            </span>
                            {isOpen ? (
                              <ChevronUp className="w-5 h-5 text-dineai-yellow flex-shrink-0" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-white/60 flex-shrink-0" />
                            )}
                          </button>

                          {isOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="px-6 pb-4"
                            >
                              <p className="text-white/80 leading-relaxed">
                                {faq.answer}
                              </p>
                            </motion.div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}

            {filteredFaqs.length === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <p className="text-white/60 text-lg">
                  No FAQs found matching "{searchQuery}"
                </p>
              </motion.div>
            )}
          </div>

          {/* Still Have Questions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="max-w-2xl mx-auto mt-16 text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20"
          >
            <h3 className="text-2xl font-bold text-white mb-4">
              Still have questions?
            </h3>
            <p className="text-white/80 mb-6">
              Can't find the answer you're looking for? Our support team is here to help.
            </p>
            <a
              href="https://dineai.ai/contact"
              className="inline-block bg-dineai-yellow hover:bg-dineai-yellow/90 text-black font-semibold px-8 py-3 rounded-xl transition-colors"
            >
              Contact Support
            </a>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default FAQ;
